// Section 22 Settings: theme, text size, reduced motion, autosave count, confirm end season.
// Persisted in localStorage under the namespaced key; unknown or bad values fall back to defaults.
import { z } from 'zod';
import { KEYS } from './keys';
import { isQuotaError, type StorageLike } from './save';

export const AUTOSAVE_MIN = 2;
export const AUTOSAVE_MAX = 20;

const settingsSchema = z.strictObject({
  /** 'system' follows prefers-color-scheme. */
  theme: z.enum(['system', 'light', 'dark', 'high-contrast']),
  textSize: z.enum(['small', 'normal', 'large']),
  /** 'system' follows prefers-reduced-motion. */
  motion: z.enum(['system', 'reduce', 'full']),
  /** N in the autosave ring (Section 24.12, default 8). */
  autosaveCount: z.number().int().min(AUTOSAVE_MIN).max(AUTOSAVE_MAX),
  /** Show a summary of this season's changes before ending it (Section 22 turn flow). */
  confirmEndSeason: z.boolean(),
});

export type Settings = z.infer<typeof settingsSchema>;
export type Theme = Settings['theme'];
export type TextSize = Settings['textSize'];
export type Motion = Settings['motion'];

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  textSize: 'normal',
  motion: 'system',
  autosaveCount: 8,
  confirmEndSeason: true,
};

/** Settings from storage, field by field, so one bad value does not reset the rest. */
export function readSettings(storage: StorageLike | null): Settings {
  const text = storage?.getItem(KEYS.settings) ?? null;
  if (text === null) return DEFAULT_SETTINGS;
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return DEFAULT_SETTINGS;
  }
  if (raw === null || typeof raw !== 'object') return DEFAULT_SETTINGS;
  const out: Record<string, unknown> = { ...DEFAULT_SETTINGS };
  for (const [key, field] of Object.entries(settingsSchema.shape)) {
    const value = (raw as Record<string, unknown>)[key];
    if (field.safeParse(value).success) out[key] = value;
  }
  return settingsSchema.parse(out);
}

/** Returns false if storage is unavailable or full (settings still apply for this session). */
export function writeSettings(storage: StorageLike | null, settings: Settings): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(KEYS.settings, JSON.stringify(settingsSchema.parse(settings)));
    return true;
  } catch (error) {
    if (isQuotaError(error)) return false;
    throw error;
  }
}
